import { useMemo, useState, useEffect } from 'react';

import { logger } from 'src/utils/logger';
import {
    createJsrClient,
    buildResourceUrl,
    buildSearchParams,
    ensureJsrAuthOnce,
    signOutPreviousJsrSessionIfNeeded,
} from 'src/utils/jsr-auth';
import {
    fetchReportOptionsSummary,
    fetchOptionParameterValues,
    saveReportOptions as saveReportOptionsRequest,
    deleteReportOption as deleteReportOptionRequest,
} from 'src/utils/report-options';

import { getAccessToken } from 'src/stores/user-session';

import { useAuthContext } from 'src/auth/hooks';
import { jwtDecode } from 'src/auth/context/jwt/utils';

// ----------------------------------------------------------------------

interface JasperAuthV7 {
    name: string;
    password: string;
    organization: string;
    locale: string;
    timezone: string;
}

interface JasperAuthV9 {
    token: string;
    preAuth: boolean;
    tokenName: string;
}

const buildFavoritesPayload = (resourceUris: string[]) => ({
    favorites: resourceUris.map((uri) => ({ uri })),
});

// ----------------------------------------------------------------------


/**
 * Custom hook to interact with JasperReports Server (JSR)
 */
export const useJsrClient = (options = null, types = null) => {
    const { user } = useAuthContext();
    const accessToken = getAccessToken();
    const serverUrl = user?.jsrIntegration?.serverUrl;
    const tenantId = user?.tenantId ?? user?.tenant_id;

    const client = useMemo(() => createJsrClient({ serverUrl }), [serverUrl]);

    const [version, setVersion] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;

        const handleTenantContextChange = async () => {
            const didSignOut = await signOutPreviousJsrSessionIfNeeded({ serverUrl, tenantId });
            if (!cancelled && didSignOut) {
                setVersion(null);
            }
        };

        handleTenantContextChange();

        return () => {
            cancelled = true;
        };
    }, [serverUrl, tenantId]);
    const defaultOptions = options || {
        folderUri: '/',
        excludeFolder: '/public',
        recursive: true,
    };
    const defaultTypes = types || ['reportUnit', 'dashboard', 'folder'];

    const locale = 'en';
    const timezone = 'America/New_York';

    // Version is determined lazily via the shared JSR auth handshake.


    /**
     * Get server version information
     */
    const getServerInfo = async (): Promise<{ version: string }> => {
        const { data } = await client.get('/rest_v2/serverInfo');
        return data;
    };

    /**
     * Get authentication configuration for JSR v7
     */
    const getJasperAuthV7 = (): JasperAuthV7 => {
        if (!accessToken) {
            throw new Error('Access token is not available');
        }

        const { sub, password, tenant_id } = jwtDecode(accessToken);
        return {
            name: sub,
            password: encodeURIComponent(password),
            organization: tenant_id,
            locale,
            timezone
        };
    };

    /**
     * Get authentication configuration for JSR v9
     */
    const getJasperAuthV9 = (): JasperAuthV9 => {
        if (!accessToken) {
            throw new Error('Access token is not available');
        }

        return {
            token: accessToken,
            preAuth: true,
            tokenName: 'token'
        };
    };

    /**
     * Get appropriate authentication based on server version
     */
    const getJasperAuth = async (): Promise<JasperAuthV7 | JasperAuthV9> => {
        try {
            let ensuredVersion = version;
            if (ensuredVersion === null) {
                const serverUrl = user?.jsrIntegration?.serverUrl;
                const result = await ensureJsrAuthOnce({
                    serverUrl,
                    accessToken,
                    options: defaultOptions,
                    types: defaultTypes,
                });
                ensuredVersion = result.version;
                setVersion(result.version);
            }

            if (ensuredVersion === 7) return getJasperAuthV7();
            if (ensuredVersion === 9) return getJasperAuthV9();
            throw new Error(`Unsupported JasperReports Server version: ${ensuredVersion}`);
        } catch (error) {
            logger.error('Failed to get Jasper authentication:', error);
            throw error;
        }
    };

    /**
     * Get list of resources from JSR
     */
    const getResources = async (options: any = defaultOptions, types: string[] = defaultTypes) => {
        const serverUrl = user?.jsrIntegration?.serverUrl;
        const auth = await ensureJsrAuthOnce({
            serverUrl,
            accessToken,
            options,
            types,
        });
        const { version: ensuredVersion, tokenRequired, probeKey } = auth;

        setVersion(ensuredVersion);

        // `ensureJsrAuthOnce` performs an initial `/rest_v2/resources` probe to detect token requirements.
        // When the probe query matches this request, reuse its response to avoid a duplicate resources call.
        // The auth result is cached for the session, so hand the probe response out only once; later
        // calls (e.g. a refresh) must fetch fresh data.
        const currentProbeKey = buildSearchParams({ options, types }).toString();
        if (probeKey === currentProbeKey && auth.probeResourceLookup) {
            const { probeResourceLookup } = auth;
            auth.probeResourceLookup = null;
            return probeResourceLookup;
        }

        const response = await client.get(
            buildResourceUrl({
                options,
                types,
                token: tokenRequired ? accessToken : null,
            }),
        );
        return response.data?.resourceLookup || [];
    };

    /**
     * Get dashboards from JSR
     */
    const getDashboards = async (options = defaultOptions) => {
        return getResources(options, ['dashboard']);
    };

    /**
     * Get reports from JSR
     */
    const getReports = async (options = defaultOptions) => {
        return getResources(options, ['reportUnit']);
    };

    /**
     * Get favorite resources from JSR
     */
    const getFavorites = async (options = defaultOptions, types: string[] = defaultTypes) => {
        return getResources({ ...options, favorites: true }, types);
    };

    /**
     * Set favorite resources in JSR
     */
    const setFavorites = async (resourceUris: string[]) => {
        await client.post('/rest_v2/favorites', buildFavoritesPayload(resourceUris));
    };

    /**
     * Delete favorite resources in JSR
     */
    const deleteFavorites = async (resourceUris: string[]) => {
        await client.post('/rest_v2/favorites/delete', buildFavoritesPayload(resourceUris));
    };

    /**
     * Delete a repository resource in JSR
     */
    const deleteResource = async (resourceUri: string) => {
        const normalizedUri = resourceUri.startsWith('/') ? resourceUri : `/${resourceUri}`;
        await client.delete(`/rest_v2/resources${normalizedUri}`);
    };

    /**
     * Run a report and get output
     */
    const runReport = async (reportUri: string, parameters?: Record<string, any>) => {
        const params = parameters ? `?${new URLSearchParams(parameters).toString()}` : '';
        const { data } = await client.get(`/rest_v2/reports${reportUri}/outputFormat=json${params}`);
        return data;
    };

    /**
     * Schedule a job
     */
    const scheduleJob = async (jobDefinition: any) => {
        const { data } = await client.put('/rest_v2/jobs', jobDefinition);
        return data;
    };

    /**
     * Get list of scheduled jobs with pagination and filters
     */
    const getScheduledJobs = async (options: {
        offset?: number;
        limit?: number;
        sortType?: string;
        isAscending?: boolean;
        jobID?: number;
        jobLabel?: string;
        resourceLabel?: string;
        reportUnitURI?: string;
        owner?: string;
    } = {}) => {
        const searchParams = new URLSearchParams();

        // Add pagination parameters
        if (options.offset !== undefined) searchParams.append('offset', options.offset.toString());
        if (options.limit !== undefined) searchParams.append('limit', options.limit.toString());
        if (options.sortType) searchParams.append('sortType', options.sortType);
        if (options.isAscending !== undefined) searchParams.append('isAscending', options.isAscending.toString());

        // Add filter parameters
        if (options.jobID) searchParams.append('jobID', options.jobID.toString());
        if (options.jobLabel) searchParams.append('jobLabel', options.jobLabel);
        if (options.resourceLabel) searchParams.append('resourceLabel', options.resourceLabel);
        if (options.reportUnitURI) searchParams.append('reportUnitURI', options.reportUnitURI);
        if (options.owner) searchParams.append('owner', options.owner);

        const response = await client.get(`/rest_v2/jobs?${searchParams.toString()}`);
        const { data } = response;

        // The API may return totalCount in the response body or we need to calculate it
        const jobs = data?.jobsummary || [];
        let totalCount = data?.totalCount;

        // If totalCount is not provided by the API, estimate based on pagination
        if (totalCount === undefined || totalCount === null) {
            const currentOffset = options.offset || 0;
            const currentLimit = options.limit || 10;

            // If we got a full page of results, there might be more
            // We estimate totalCount as current offset + returned jobs + 1 (to show next page)
            if (jobs.length === currentLimit) {
                totalCount = currentOffset + jobs.length + 1;
            } else {
                // Last page - we know the exact total
                totalCount = currentOffset + jobs.length;
            }
        }

        // Return both jobs and total count for pagination
        return {
            jobs,
            totalCount
        };
    };

    /**
     * Get job details
     */
    const getJobDetails = async (jobId: number) => {
        const { data } = await client.get(`/rest_v2/jobs/${jobId}`);
        return data;
    };

    /**
     * Update a scheduled job
     */
    const updateJob = async (jobId: number, jobDefinition: any) => {
        const { data } = await client.post(`/rest_v2/jobs/${jobId}`, jobDefinition);
        return data;
    };

    /**
     * Delete a scheduled job
     */
    const deleteJob = async (jobId: number) => {
        await client.delete(`/rest_v2/jobs/${jobId}`);
    };

    /**
     * Pause a scheduled job
     */
    const pauseJob = async (jobId: number) => {
        const payload = { jobId: [jobId] };
        const { data } = await client.post('/rest_v2/jobs/pause', payload);
        return data;
    };

    /**
     * Resume a scheduled job
     */
    const resumeJob = async (jobId: number) => {
        const payload = { jobId: [jobId] };
        const { data } = await client.post('/rest_v2/jobs/resume', payload);
        return data;
    };

    /**
     * Verify if JSR integration is available
     */
    const isJsrAvailable = useMemo((): boolean => {
        return !!user?.jsrIntegration?.serverUrl;
    }, [user?.jsrIntegration?.serverUrl]);

    /**
     * List saved report options for a report unit.
     */
    const getReportOptionsSummary = async (resourceUri: string) => {
        await ensureJsrAuthOnce({
            serverUrl: user?.jsrIntegration?.serverUrl,
            accessToken,
            options: defaultOptions,
            types: defaultTypes,
        });
        return fetchReportOptionsSummary(client, resourceUri);
    };

    /**
     * Save report input control values as a named option preset.
     */
    const saveReportOptions = async (
        resourceUri: string,
        label: string,
        params: Record<string, string[] | string>,
    ) => {
        await ensureJsrAuthOnce({
            serverUrl: user?.jsrIntegration?.serverUrl,
            accessToken,
            options: defaultOptions,
            types: defaultTypes,
        });
        return saveReportOptionsRequest(client, resourceUri, label, params);
    };

    /**
     * Load parameter values for a saved report option.
     */
    const getReportOptionValues = async (optionUri: string) => {
        await ensureJsrAuthOnce({
            serverUrl: user?.jsrIntegration?.serverUrl,
            accessToken,
            options: defaultOptions,
            types: defaultTypes,
        });
        return fetchOptionParameterValues(client, optionUri);
    };

    /**
     * Delete a saved report option preset.
     */
    const deleteReportOption = async (resourceUri: string, optionId: string) => {
        await ensureJsrAuthOnce({
            serverUrl: user?.jsrIntegration?.serverUrl,
            accessToken,
            options: defaultOptions,
            types: defaultTypes,
        });
        return deleteReportOptionRequest(client, resourceUri, optionId);
    };

    return {
        // Configuration
        isJsrAvailable,

        // Server info
        getServerInfo,

        // Authentication
        getJasperAuth,
        getJasperAuthV7,
        getJasperAuthV9,

        // Resources
        getResources,
        getDashboards,
        getReports,
        getFavorites,
        setFavorites,
        deleteFavorites,
        deleteResource,
        runReport,

        // Report options
        getReportOptionsSummary,
        saveReportOptions,
        getReportOptionValues,
        deleteReportOption,

        // Scheduling
        scheduleJob,
        getScheduledJobs,
        getJobDetails,
        updateJob,
        deleteJob,
        pauseJob,
        resumeJob
    };
};
